import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { HttpResponse } from 'interfaces/httpClient';
import {
	BulkRejectClients,
	CaseUpdates,
	DocumentActivePhase,
	DocumentRecordData,
	DocumentRecordPayload,
	DocumentRequiredPayload,
	DocumentRequiredPayloadItem,
	DocumentType,
	FollowUpLawyer,
	FollowUpLawyerAnswerData,
	FollowUpLawyerPayload,
	LandingPageCaseClaimForm,
	LandingPageCaseClaimFormPayload,
	LandingPageCaseDetails,
	LandingPageCaseDetailsPayload,
	LandingPageQuestionsType,
	OPTHistoryData,
	VersionRecordData,
} from '@/interfaces';

class CaseApplicantService {
	constructor(private client: AxiosInstance) {}

	// ------------ Case Details ---------------//
	async getLandingPageCaseDetails(
		case_id: string
	): Promise<HttpResponse<LandingPageCaseDetails>> {
		return this.client.get(
			`/cases-applicant/landing-page-case-detail/${case_id}`
		);
	}
	async putLandingPageCaseDetails(
		case_id: string,
		data: LandingPageCaseDetailsPayload
	): Promise<HttpResponse<LandingPageCaseDetails>> {
		const formData = new FormData();
		data.about_claim && formData.append('about_claim', data.about_claim);
		data.about_company && formData.append('about_company', data.about_company);
		data.cover_image && formData.append('cover_image', data.cover_image);
		data.eligibility_criteria &&
			formData.append('eligibility_criteria', data.eligibility_criteria);
		data.registration_deadline &&
			formData.append('registration_deadline', data.registration_deadline);
		data.cover_image_url &&
			formData.append('cover_image_url', data.cover_image_url || '');
		data.image_source_type &&
			formData.append('image_source_type', data.image_source_type || '');
		return this.client.put(
			`/cases-applicant/landing-page-case-detail/${case_id}`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		);
	}
	async postLandingPageCaseDetails(
		case_id: string,
		data: LandingPageCaseDetailsPayload
	): Promise<HttpResponse<LandingPageCaseDetails>> {
		const formData = new FormData();
		formData.append('about_claim', data.about_claim || '');
		formData.append('about_company', data.about_company || '');
		formData.append('eligibility_criteria', data.eligibility_criteria || '');
		formData.append('registration_deadline', data.registration_deadline || '');
		data.cover_image_url &&
			formData.append('cover_image_url', data.cover_image_url || '');
		data.cover_image && formData.append('cover_image', data.cover_image);
		data.image_source_type &&
			formData.append('image_source_type', data.image_source_type || '');
		return this.client.post(
			`/cases-applicant/landing-page-case-detail/${case_id}`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		);
	}
	async patchImageLandingPageCaseDetails(
		case_id: string
	): Promise<HttpResponse<LandingPageCaseDetails>> {
		return this.client.patch(`/cases-applicant/clear-image-case/${case_id}`);
	}

	// -------- Case Claim Form -------//
	async getLandingPageCaseQuestionsTypes(): Promise<
		HttpResponse<LandingPageQuestionsType[]>
	> {
		return this.client.get(`/cases-applicant/question-types`);
	}
	async getLandingPageCaseClaimForm(
		case_id: string
	): Promise<HttpResponse<LandingPageCaseClaimForm[]>> {
		return this.client.get(`/cases-applicant/questions/${case_id}`);
	}
	async postLandingPageCaseClaimForm(
		case_id: string,
		data: LandingPageCaseClaimFormPayload[]
	): Promise<HttpResponse<LandingPageCaseClaimForm[]>> {
		return this.client.post(`/cases-applicant/questions-form/${case_id}`, {
			forms: data,
		});
	}
	async putLandingPageCaseClaimForm(
		case_id: string,
		data: LandingPageCaseClaimFormPayload[]
	): Promise<HttpResponse<LandingPageCaseClaimForm[]>> {
		return this.client.put(`/cases-applicant/questions-form/${case_id}`, {
			forms: data,
		});
	}

	// ---------------- Action Required  ----------------//

	async postCompleteActionRequired(
		applicationId: number
	): Promise<HttpResponse<FollowUpLawyer>> {
		return this.client.post(
			`/applications-lawyers/complete-actions/${applicationId}`
		);
	}

	async postBulkReject(
		data: BulkRejectClients
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/applications-lawyers/bulk-reject`, data);
	}

	// --------------- Document Required ---------------//
	async getDocumentRequired(
		case_id: string
	): Promise<HttpResponse<DocumentRequiredPayloadItem[]>> {
		return this.client.get(`/cases-applicant/documents-required/${case_id}`);
	}
	async postDocumentRequired(
		case_id: string,
		data: DocumentRequiredPayload
	): Promise<HttpResponse<DocumentRequiredPayload[]>> {
		return this.client.post(
			`/cases-applicant/documents-required/${case_id}`,
			data
		);
	}
	async putDocumentRequired(
		case_id: string,
		data: DocumentRequiredPayload
	): Promise<HttpResponse<DocumentRequiredPayload[]>> {
		return this.client.put(
			`/cases-applicant/documents-required/${case_id}`,
			data
		);
	}

	async updateCurrentDocument(
		documentId: number,
		data: DocumentRequiredPayload
	) {
		return this.client.put(
			`/cases-applicant/documents-required/items/${documentId}`,
			data
		);
	}

	async approveDocumentRequired(actionId: number): Promise<HttpResponse<any>> {
		return this.client.post(
			`/applications-lawyers/validate-document/${actionId}`
		);
	}

	async invalidateDocumentRequired(
		actionId: number
	): Promise<HttpResponse<any>> {
		return this.client.post(
			`/applications-lawyers/invalidate-document/${actionId}`
		);
	}

	// ---------- Send Follow Up -------------//
	async getFollowUps(
		application_id: number
	): Promise<HttpResponse<FollowUpLawyer[]>> {
		return this.client.get(`/applications-lawyers/${application_id}/follow-up`);
	}
	async getFollowUpById(
		actionId: number
	): Promise<HttpResponse<FollowUpLawyerAnswerData>> {
		return this.client.get(`/action-required/form-questions/${actionId}`);
	}
	async postFollowUp(
		data: FollowUpLawyerPayload
	): Promise<HttpResponse<FollowUpLawyer>> {
		return this.client.post(`/applications-lawyers/create-follow-up`, data);
	}

	// ------------- Send Update Case ---------------//

	async getCaseUpdates(
		page: number,
		caseId: string
	): Promise<HttpResponse<CaseUpdates>> {
		return this.client.get(
			`/applications-lawyers/case-updates/${caseId}?page=${page}&limit=${4}`
		);
	}

	async sendCaseUpdates(caseId: string, content: string) {
		return this.client.post(`/applications-lawyers/send-case-update`, {
			case_id: caseId,
			content,
		});
	}

	// -------------- Demand Letter / Complaint ---------------//
	async postDemandComplaint(
		caseId: string,
		documentType: 'Demand Letter' | 'Complaint'
	): Promise<HttpResponse<FollowUpLawyer>> {
		return this.client.post(
			`/cases-active/complete-any-active-phase-step/${caseId}`,
			{ documentType }
		);
	}
	async postUploadDemandComplaint(
		caseId: string,
		documentType: 'Demand Letter' | 'Complaint',
		file: File
	): Promise<HttpResponse<FollowUpLawyer>> {
		const formData = new FormData();
		formData.append('caseId', caseId);
		formData.append('documentType', documentType);
		formData.append('file', file);
		return this.client.post(`/cases-active/active-case/upload`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}
	async getCaseDocuments(
		caseId: string
	): Promise<HttpResponse<DocumentActivePhase[]>> {
		return this.client.get(`/cases-active/case-documents/${caseId}`);
	}

	async deleteActivaCaseDocument(
		caseId: string,
		documentType: DocumentType
	): Promise<HttpResponse<string>> {
		return this.client.delete(`/cases-active/active-case/delete-upload`, {
			data: { caseId, documentType },
		});
	}

	// --------------- Active Case New -------//
	async getDocumentRecords(
		caseId: string
	): Promise<HttpResponse<DocumentRecordData[]>> {
		return this.client.get(`/cases-active/document-records/${caseId}`);
	}

	async postDocumentRecords(
		caseId: string,
		data: DocumentRecordPayload
	): Promise<HttpResponse<DocumentRecordData[]>> {
		return this.client.post(
			`/cases-active/document-records/register/${caseId}`,
			data
		);
	}

	async getVersionRecords(
		caseId: string
	): Promise<HttpResponse<VersionRecordData[]>> {
		return this.client.get(`/cases-active/version-records/${caseId}`);
	}

	async getOPTHistory(caseId: string): Promise<HttpResponse<OPTHistoryData[]>> {
		return this.client.get(`/cases-active/history-otp/${caseId}`);
	}

	async postUploadOPT(
		caseId: string,
		file: File
	): Promise<HttpResponse<VersionRecordData[]>> {
		const formData = new FormData();
		formData.append('caseId', caseId);
		formData.append('file', file);
		return this.client.post(`/cases-active/active-case/upload-opt`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}
}

export const caseApplicantService = new CaseApplicantService(axiosClient);
